import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import { toast } from "sonner";
import {
  Users, UserPlus, Loader2, Shield, ShieldCheck, Mail, Trash2,
  RefreshCw, Power, Copy, Check, Clock, Activity, BarChart3,
  Calendar, Plus, X as XIcon, DollarSign,
} from "lucide-react";

const ROLE_LABEL = {
  owner: "Owner",
  manager: "Manager",
  staff: "Staff",
};
const ROLE_HELP = {
  owner: "Full access including billing & danger zone",
  manager: "Full access to clients, jobs, team — no billing",
  staff: "Access to assigned work + collaborative reads",
};

// v68.11 — stage roles (rate-plan lookup). Kept in sync with backend STAGE_ROLES.
const STAGE_ROLES = [
  { value: "",                    label: "— unassigned —" },
  { value: "accountant",          label: "Accountant" },
  { value: "bookkeeper",          label: "Bookkeeper" },
  { value: "payroll_specialist",  label: "Payroll" },
  { value: "tax_specialist",      label: "Tax" },
  { value: "admin",               label: "Admin" },
  { value: "client_manager",      label: "Client Mgr" },
  { value: "partner",             label: "Partner" },
];

export default function Team() {
  const { state } = useAuth();
  const isAdmin = ["owner", "manager"].includes(state?.user?.role);
  const [data, setData] = useState({ members: [], pending_invites: [] });
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/team`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!isAdmin) {
    return (
      <div className="px-10 py-16">
        <h1 className="font-heading text-3xl">Team</h1>
        <p className="text-[var(--ink-2)] mt-3">Only owners and managers can view the team page.</p>
      </div>
    );
  }

  return (
    <div className="px-10 py-16 max-w-4xl" data-testid="team-page">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2 inline-flex items-center gap-1.5">
            <Users size={12} /> Team
          </div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>
            Your <em className="italic" style={{ color: "var(--accent)" }}>practice</em> team
          </h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
            Invite team members, change roles, and deactivate when someone leaves.
            Every change is recorded in the audit log.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {isAdmin && data && <BulkStageRoleButton members={data.members} onSaved={load} />}
          <button onClick={() => setInviteOpen(true)} className="btn-primary"
                  data-testid="team-invite-btn">
            <UserPlus size={14} /> Invite team member
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-[var(--ink-3)] text-[13px]"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>
      ) : (
        <>
          {isAdmin && <StageRoleSuggestionsBanner onApplied={load} />}
          <CapacitySection />
          <LeaveSection members={data.members} />

          <Section title={`Active members (${data.members.filter(m => m.is_active).length})`}>
            <div className="space-y-2">
              {data.members.filter(m => m.is_active).map((m) => (
                <MemberRow key={m.user_id} member={m} currentUser={state?.user} onChange={load} />
              ))}
            </div>
          </Section>

          {data.pending_invites.length > 0 && (
            <Section title={`Pending invitations (${data.pending_invites.length})`}>
              <div className="space-y-2">
                {data.pending_invites.map((inv) => (
                  <InviteRow key={inv.invite_id} invite={inv} onChange={load} />
                ))}
              </div>
            </Section>
          )}

          {data.members.some(m => !m.is_active) && (
            <Section title={`Deactivated (${data.members.filter(m => !m.is_active).length})`}>
              <div className="space-y-2 opacity-70">
                {data.members.filter(m => !m.is_active).map((m) => (
                  <MemberRow key={m.user_id} member={m} currentUser={state?.user} onChange={load} />
                ))}
              </div>
            </Section>
          )}
        </>
      )}

      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} onSent={load} />}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <div className="overline mb-3">{title}</div>
      {children}
    </div>
  );
}

function fmtHours(minutes) {
  if (!minutes || minutes <= 0) return "0h";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function CapacitySection() {
  const [rows, setRows] = useState(null);
  const [weekStart, setWeekStart] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/team/capacity`);
        setRows(r.data.rows || []);
        setWeekStart(r.data.week_starts_at);
      } catch (e) {
        // 403 just means user isn't admin — ignore silently here
        if (e?.response?.status !== 403) toast.error(formatApiError(e));
      }
    })();
  }, []);

  if (rows === null) return null;
  if (rows.length === 0) return null;

  const maxJobs = Math.max(1, ...rows.map((r) => r.active_jobs));
  const weekLabel = weekStart
    ? new Date(weekStart).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "this week";

  return (
    <div className="mb-10" data-testid="team-capacity">
      <div className="overline mb-3 inline-flex items-center gap-1.5">
        <BarChart3 size={12} /> Capacity · week of {weekLabel}
      </div>
      <div className="card overflow-hidden">
        <table className="report-table">
          <thead>
            <tr>
              <th>Member</th>
              <th className="text-right">Active jobs</th>
              <th className="text-right">Active leads</th>
              <th className="text-right">Outstanding budget</th>
              <th className="text-right">Logged this week</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} data-testid={`capacity-row-${r.user_id}`}>
                <td>
                  <div className="flex items-center gap-2">
                    <span className="text-[var(--ink)] font-medium">{r.name}</span>
                    <span className="badge badge-outline text-[9px]">{r.role}</span>
                  </div>
                  <div className="text-[10px] font-mono text-[var(--ink-3)]">{r.email}</div>
                </td>
                <td className="text-right">
                  <div className="inline-flex items-center gap-2">
                    <div className="w-16 h-1 bg-[var(--subtle)] rounded-full overflow-hidden">
                      <div
                        className="h-1 bg-[var(--ink)]"
                        style={{ width: `${Math.min(100, (r.active_jobs / maxJobs) * 100)}%` }}
                      />
                    </div>
                    <span className="font-mono text-[12px]">{r.active_jobs}</span>
                  </div>
                </td>
                <td className="text-right font-mono text-[12px]">{r.active_leads}</td>
                <td className="text-right font-mono text-[12px] text-[var(--ink-3)]">
                  {fmtHours(r.outstanding_minutes)}
                </td>
                <td className="text-right">
                  <span className="inline-flex items-center gap-1 font-mono text-[12px]">
                    <Activity size={11} /> {fmtHours(r.minutes_this_week)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MemberRow({ member, currentUser, onChange }) {
  const isSelf = member.user_id === currentUser?.user_id;
  const canEdit = !isSelf && (currentUser?.role === "owner"
                              || (currentUser?.role === "manager" && member.role !== "owner"));
  const isAdmin = ["owner", "manager"].includes(currentUser?.role);
  const [busy, setBusy] = useState(false);
  const [rateEditing, setRateEditing] = useState(false);
  const [rateDraft, setRateDraft] = useState(String(member.hourly_rate || 0));
  const [chargeEditing, setChargeEditing] = useState(false);
  const [chargeDraft, setChargeDraft] = useState(String(member.charge_rate || 0));

  const changeRole = async (newRole) => {
    if (!window.confirm(`Change ${member.email}'s role to ${ROLE_LABEL[newRole]}?`)) return;
    setBusy(true);
    try {
      await axios.patch(`${API_BASE}/team/${member.user_id}`, { role: newRole });
      toast.success("Role updated");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggleActive = async () => {
    const next = !member.is_active;
    const verb = next ? "Reactivate" : "Deactivate";
    if (!window.confirm(`${verb} ${member.email}?${next ? "" : " They'll lose access immediately."}`)) return;
    setBusy(true);
    try {
      await axios.patch(`${API_BASE}/team/${member.user_id}`, { is_active: next });
      toast.success(`${verb}d`);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const saveRate = async () => {
    const n = parseFloat(rateDraft);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Enter a valid non-negative number");
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`${API_BASE}/team/${member.user_id}`, { hourly_rate: n });
      toast.success("Hourly rate updated");
      setRateEditing(false);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const saveChargeRate = async () => {
    const n = parseFloat(chargeDraft);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Enter a valid non-negative number");
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`${API_BASE}/team/${member.user_id}`, {
        charge_rate: n,
        charge_rate_effective_from: new Date().toISOString(),
      });
      toast.success("Charge rate updated");
      setChargeEditing(false);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // v68.11 — Inline stage-role picker (drives rate-plan resolution).
  const saveStageRole = async (nextRole) => {
    if (nextRole === (member.stage_role || "")) return;
    setBusy(true);
    try {
      await axios.patch(`${API_BASE}/team/${member.user_id}`, {
        stage_role: nextRole || null,
      });
      toast.success(nextRole
        ? `Stage role set to ${nextRole.replace(/_/g," ")}`
        : "Stage role cleared");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card p-4 flex items-center gap-4" data-testid={`team-member-${member.user_id}`}>
      <Avatar name={member.name} picture={member.picture} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-medium text-[14px] truncate">{member.name}</div>
          {isSelf && <span className="text-[10px] text-[var(--ink-3)] font-mono uppercase">(you)</span>}
          {member.totp_enabled && (
            <span title="2FA enabled" className="text-emerald-600"><ShieldCheck size={12} /></span>
          )}
        </div>
        <div className="text-[12px] text-[var(--ink-3)] truncate">{member.email}</div>
        <div className="text-[11px] text-[var(--ink-3)] mt-1 flex items-center gap-3 flex-wrap">
          {member.active_jobs_count > 0 && (
            <span>{member.active_jobs_count} active job{member.active_jobs_count !== 1 ? "s" : ""}</span>
          )}
          {member.created_at && (
            <span>joined {new Date(member.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}</span>
          )}
          {isAdmin && !isSelf && (
            rateEditing ? (
              <span className="inline-flex items-center gap-1">
                <DollarSign size={10} className="text-[var(--ink-3)]" />
                <input
                  type="number" min="0" step="1" value={rateDraft}
                  onChange={(e) => setRateDraft(e.target.value)}
                  className="w-16 bg-white border border-[var(--border)] rounded px-1.5 py-0.5 text-[11px]"
                  data-testid={`team-rate-input-${member.user_id}`}
                />
                <span className="text-[10px] text-[var(--ink-3)]">/hr cost</span>
                <button onClick={saveRate} disabled={busy}
                        className="text-[var(--accent)] hover:underline"
                        data-testid={`team-rate-save-${member.user_id}`}>save</button>
                <button onClick={() => { setRateEditing(false); setRateDraft(String(member.hourly_rate || 0)); }}
                        className="text-[var(--ink-3)] hover:text-[var(--ink)]">×</button>
              </span>
            ) : (
              <button onClick={() => setRateEditing(true)}
                      className="inline-flex items-center gap-0.5 text-[var(--ink-3)] hover:text-[var(--accent)]"
                      data-testid={`team-rate-${member.user_id}`}
                      title="Cost rate (internal)">
                <DollarSign size={10} />
                {member.hourly_rate > 0 ? `$${member.hourly_rate}/hr cost` : "set cost"}
              </button>
            )
          )}
          {/* v68.23 — Charge rate (client-billed). Visible to everyone; only
              admins get the inline editor. */}
          {!isSelf && (
            isAdmin && chargeEditing ? (
              <span className="inline-flex items-center gap-1">
                <DollarSign size={10} className="text-[var(--ink-3)]" />
                <input
                  type="number" min="0" step="1" value={chargeDraft}
                  onChange={(e) => setChargeDraft(e.target.value)}
                  className="w-16 bg-white border border-[var(--border)] rounded px-1.5 py-0.5 text-[11px]"
                  data-testid={`team-charge-input-${member.user_id}`}
                />
                <span className="text-[10px] text-[var(--ink-3)]">/hr charge</span>
                <button onClick={saveChargeRate} disabled={busy}
                        className="text-[var(--accent)] hover:underline"
                        data-testid={`team-charge-save-${member.user_id}`}>save</button>
                <button onClick={() => { setChargeEditing(false); setChargeDraft(String(member.charge_rate || 0)); }}
                        className="text-[var(--ink-3)] hover:text-[var(--ink)]">×</button>
              </span>
            ) : (
              <button
                onClick={() => isAdmin && setChargeEditing(true)}
                disabled={!isAdmin}
                className={`inline-flex items-center gap-0.5 ${isAdmin ? "text-[var(--ink-3)] hover:text-[var(--accent)]" : "text-[var(--ink-3)] cursor-default"}`}
                data-testid={`team-charge-${member.user_id}`}
                title={isAdmin ? "Charge rate (client-billed)" : "Charge rate — admin only"}
              >
                <DollarSign size={10} />
                {member.charge_rate > 0 ? `$${member.charge_rate}/hr charge` : (isAdmin ? "set charge" : "no charge rate")}
              </button>
            )
          )}
          {isAdmin && !isSelf && (
            <span className="inline-flex items-center gap-1">
              <span className="text-[10px] text-[var(--ink-3)]">role:</span>
              <select
                value={member.stage_role || ""}
                onChange={(e) => saveStageRole(e.target.value)}
                disabled={busy}
                className="bg-white border border-[var(--border)] rounded px-1.5 py-0.5 text-[11px] focus:outline-none focus:border-[var(--ink)]"
                data-testid={`team-stage-role-${member.user_id}`}
                title="Stage role — drives rate-plan lookup"
              >
                {STAGE_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <select
          value={member.role}
          onChange={(e) => changeRole(e.target.value)}
          disabled={!canEdit || busy || member.role === "owner"}
          className="input text-[12px] !py-1"
          data-testid={`team-role-${member.user_id}`}
          title={ROLE_HELP[member.role]}
        >
          <option value="staff">{ROLE_LABEL.staff}</option>
          <option value="manager">{ROLE_LABEL.manager}</option>
          {currentUser?.role === "owner" && <option value="owner">{ROLE_LABEL.owner}</option>}
        </select>
        {canEdit && (
          <button
            onClick={toggleActive}
            className="text-[var(--ink-3)] hover:text-[var(--accent)]"
            title={member.is_active ? "Deactivate" : "Reactivate"}
            data-testid={`team-toggle-${member.user_id}`}
          >
            <Power size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function InviteRow({ invite, onChange }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const revoke = async () => {
    if (!window.confirm(`Revoke the invitation to ${invite.email}? The link in their email will stop working.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API_BASE}/team/invite/${invite.invite_id}`);
      toast.success("Invitation revoked");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/team/invite/${invite.invite_id}/resend`);
      toast.success("Invitation resent");
      navigator.clipboard.writeText(r.data.accept_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card p-4 flex items-center gap-4" data-testid={`team-invite-row-${invite.invite_id}`}>
      <div className="w-10 h-10 rounded-full bg-[var(--subtle)] flex items-center justify-center">
        <Mail size={14} className="text-[var(--ink-3)]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[14px] truncate">{invite.name}</div>
        <div className="text-[12px] text-[var(--ink-3)] truncate">{invite.email}</div>
        <div className="text-[11px] text-[var(--ink-3)] mt-1 inline-flex items-center gap-1">
          <Clock size={10} /> expires {new Date(invite.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          <span className="ml-2 px-1.5 py-0.5 bg-[var(--subtle)] rounded text-[10px] uppercase">{invite.role}</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <button onClick={resend} disabled={busy} className="btn-secondary text-[11px] !py-1"
                data-testid={`team-resend-${invite.invite_id}`}>
          {copied ? <Check size={11} /> : <RefreshCw size={11} />}
          {copied ? "Link copied" : "Resend"}
        </button>
        <button onClick={revoke} disabled={busy}
                className="text-[var(--ink-3)] hover:text-[var(--accent)] p-1"
                data-testid={`team-revoke-${invite.invite_id}`}
                title="Revoke">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function InviteModal({ onClose, onSent }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("staff");
  const [sending, setSending] = useState(false);
  const [acceptUrl, setAcceptUrl] = useState(null);

  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API_BASE}/team/invite`, { email, name, role });
      toast.success(`Invitation sent to ${email}`);
      setAcceptUrl(r.data.accept_url);
      onSent();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
         data-testid="team-invite-modal" onClick={onClose}>
      <div className="card p-6 max-w-md w-full bg-[var(--paper)]"
           onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-2xl mb-1" style={{ fontWeight: 500 }}>Invite team member</h2>
        <p className="text-[12px] text-[var(--ink-3)] mb-5">They'll receive an email with a secure invitation link. The link expires in 14 days.</p>

        {acceptUrl ? (
          <div className="space-y-4">
            <p className="text-[13px]">
              <Check size={14} className="text-emerald-600 inline mr-1.5" />
              Invitation sent. If they don't get the email, share this link directly:
            </p>
            <div className="bg-[var(--subtle)] p-3 rounded font-mono text-[11px] break-all">{acceptUrl}</div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { navigator.clipboard.writeText(acceptUrl); toast.success("Link copied"); }}
                className="btn-secondary text-[12px]"
              ><Copy size={11} /> Copy link</button>
              <button onClick={onClose} className="btn-primary text-[12px]">Done</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-[12px] text-[var(--ink-3)] mb-1.5">Email address</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="colleague@firm.com" className="input w-full"
                     data-testid="invite-email" autoFocus />
            </div>
            <div>
              <label className="block text-[12px] text-[var(--ink-3)] mb-1.5">Full name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="Jane Smith" className="input w-full" data-testid="invite-name" />
            </div>
            <div>
              <label className="block text-[12px] text-[var(--ink-3)] mb-1.5">Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)}
                      className="input w-full" data-testid="invite-role">
                <option value="staff">Staff — assigned work + collaborative reads</option>
                <option value="manager">Manager — full access except billing</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="text-[12px] text-[var(--ink-3)]">Cancel</button>
              <button onClick={send} disabled={!email || !name || sending}
                      className="btn-primary text-[12px]" data-testid="invite-send-btn">
                {sending ? <><Loader2 size={11} className="animate-spin" /> Sending…</> : <>Send invitation</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Avatar({ name, picture }) {
  if (picture) {
    return <img src={picture} alt={name} className="w-10 h-10 rounded-full" />;
  }
  const initials = (name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="w-10 h-10 rounded-full bg-[var(--accent)] text-white flex items-center justify-center text-[12px] font-medium">
      {initials}
    </div>
  );
}


function LeaveSection({ members }) {
  const { state } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [draft, setDraft] = useState({
    user_id: state?.user?.user_id || "",
    start_date: today,
    end_date: today,
    reason: "PTO",
    note: "",
  });
  const memberName = (uid) => (members.find((m) => m.user_id === uid)?.name) || uid;

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/leave?upcoming_only=true`);
      setRows(r.data || []);
    } catch (e) { /* silent — section is non-critical */ console.warn(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    try {
      await axios.post(`${API_BASE}/leave`, draft);
      toast.success("Leave added");
      setOpen(false);
      setDraft({ ...draft, note: "", start_date: today, end_date: today });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (leave_id) => {
    if (!window.confirm("Delete this leave entry?")) return;
    try {
      await axios.delete(`${API_BASE}/leave/${leave_id}`);
      toast.success("Leave removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Section title="Leave & PTO calendar">
      <div className="card p-4" data-testid="leave-section">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[12px] text-[var(--ink-3)] inline-flex items-center gap-1.5">
            <Calendar size={12}/> Upcoming + active leave windows across the firm
          </div>
          <button onClick={() => setOpen((v) => !v)}
                  className="text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-1"
                  data-testid="leave-add-btn">
            {open ? <><XIcon size={12}/> Cancel</> : <><Plus size={12}/> Add leave</>}
          </button>
        </div>

        {open && (
          <div className="mb-4 p-3 rounded-md bg-[var(--subtle)] border border-[var(--border)] space-y-2"
               data-testid="leave-add-form">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <select value={draft.user_id} onChange={(e) => setDraft({ ...draft, user_id: e.target.value })}
                      className="bg-white border border-[var(--border)] rounded-md px-2 py-1.5 text-[12px]"
                      data-testid="leave-user-select">
                {members.filter(m => m.is_active).map((m) => (
                  <option key={m.user_id} value={m.user_id}>{m.name}</option>
                ))}
              </select>
              <input type="date" value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })}
                     className="bg-white border border-[var(--border)] rounded-md px-2 py-1.5 text-[12px]"
                     data-testid="leave-start-date"/>
              <input type="date" value={draft.end_date} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}
                     className="bg-white border border-[var(--border)] rounded-md px-2 py-1.5 text-[12px]"
                     data-testid="leave-end-date"/>
              <select value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
                      className="bg-white border border-[var(--border)] rounded-md px-2 py-1.5 text-[12px]"
                      data-testid="leave-reason-select">
                <option value="PTO">PTO</option>
                <option value="sick">Sick</option>
                <option value="conference">Conference</option>
                <option value="other">Other</option>
              </select>
            </div>
            <input type="text" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                   placeholder="Note (optional, e.g. Bali holiday)"
                   className="w-full bg-white border border-[var(--border)] rounded-md px-2 py-1.5 text-[12px]"
                   data-testid="leave-note-input"/>
            <div className="flex justify-end">
              <button onClick={create} className="btn-primary text-[12px]" data-testid="leave-save-btn">
                Save leave
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-[12px] text-[var(--ink-3)]"><Loader2 className="animate-spin inline mr-1" size={12}/> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-[12px] text-[var(--ink-3)] italic" data-testid="leave-empty">
            No upcoming leave booked. Add a window to start tracking PTO.
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="leave-list">
            {rows.map((lv) => (
              <div key={lv.leave_id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-white border border-[var(--border)]"
                   data-testid={`leave-row-${lv.leave_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-medium truncate">
                    {memberName(lv.user_id)} <span className="text-[11px] text-[var(--ink-3)]">· {lv.reason}</span>
                  </div>
                  <div className="text-[11px] text-[var(--ink-3)]">
                    {lv.start_date} → {lv.end_date}{lv.note ? ` · ${lv.note}` : ""}
                  </div>
                </div>
                <button onClick={() => remove(lv.leave_id)}
                        className="text-[var(--ink-3)] hover:text-[#B91C1C]"
                        title="Delete leave"
                        data-testid={`leave-delete-${lv.leave_id}`}>
                  <Trash2 size={12}/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}

// ============================================================================
// v68.12 — Stage-role suggestions banner + Overdue Digest quick action.
// ============================================================================
function StageRoleSuggestionsBanner({ onApplied }) {
  const [suggestions, setSuggestions] = useState([]);
  const [applying, setApplying] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/team/stage-role-suggestions`);
      setSuggestions(data.suggestions || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const apply = async () => {
    if (!window.confirm(
      `Auto-assign stage roles for ${suggestions.length} teammate${suggestions.length === 1 ? "" : "s"}? ` +
      `Owners → Partner, Managers → Client Manager, Staff → Accountant. Existing assignments are preserved.`
    )) return;
    setApplying(true);
    try {
      const { data } = await axios.post(`${API_BASE}/team/stage-role-suggestions/apply`);
      toast.success(`Applied ${data.applied} stage role${data.applied === 1 ? "" : "s"}`);
      setSuggestions([]);
      onApplied?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setApplying(false); }
  };

  if (loading || suggestions.length === 0) return null;

  return (
    <div className="card p-4 mb-6 flex items-start gap-4"
         style={{ background: "var(--subtle)", borderColor: "var(--moss)" }}
         data-testid="stage-role-suggestions-banner">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="overline text-[10px]">Rate-plan setup</div>
          <span className="badge text-[9px]" style={{ background: "var(--moss)", color: "var(--paper)" }}>
            {suggestions.length} suggestion{suggestions.length === 1 ? "" : "s"}
          </span>
        </div>
        <p className="text-[13px] text-[var(--ink)] mt-1.5 leading-snug">
          These teammates don&apos;t have a stage role yet — without one, rate plans can&apos;t cost their time.
          We can auto-assign based on their auth role.
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {suggestions.slice(0, 6).map((s) => (
            <span key={s.user_id}
              className="inline-flex items-center gap-1 border border-[var(--border)] rounded-full px-2 py-0.5 text-[11px] bg-white"
              data-testid={`stage-role-suggestion-${s.user_id}`}>
              {s.name || s.email} → <strong>{s.suggested.replace(/_/g," ")}</strong>
            </span>
          ))}
          {suggestions.length > 6 && (
            <span className="text-[11px] text-[var(--ink-3)] py-0.5">
              +{suggestions.length - 6} more
            </span>
          )}
        </div>
      </div>
      <button onClick={apply} disabled={applying} className="btn-primary shrink-0"
              data-testid="stage-role-suggestions-apply">
        {applying ? "Applying…" : "Auto-assign all"}
      </button>
    </div>
  );
}


// v68.13 — Bulk grid editor for stage_role assignments. Opens in a modal so
// a growing firm can reshape their whole team in one screen. Only sends
// PATCHes for rows that actually changed.
function BulkStageRoleButton({ members, onSaved }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({});   // {user_id: stage_role or ""}
  const [saving, setSaving] = useState(false);

  const activeMembers = (members || []).filter((m) => m.is_active !== false);

  const openModal = () => {
    setDraft(Object.fromEntries(activeMembers.map((m) => [m.user_id, m.stage_role || ""])));
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const changes = activeMembers.filter((m) => (m.stage_role || "") !== (draft[m.user_id] || ""));
    let ok = 0, failed = 0;
    for (const m of changes) {
      try {
        await axios.patch(`${API_BASE}/team/${m.user_id}`, {
          stage_role: draft[m.user_id] || null,
        });
        ok += 1;
      } catch { failed += 1; }
    }
    setSaving(false);
    if (ok) toast.success(`Updated ${ok} teammate${ok === 1 ? "" : "s"}`);
    if (failed) toast.error(`Failed ${failed}`);
    if (!ok && !failed) toast.info("No changes to apply");
    setOpen(false);
    onSaved?.();
  };

  return (
    <>
      <button onClick={openModal} className="btn-secondary" data-testid="bulk-stage-role-btn">
        <Users size={14} /> Bulk edit roles
      </button>
      {open && (
        <div className="fixed inset-0 z-[70] bg-[var(--ink)]/50 flex items-center justify-center p-6"
             onClick={() => setOpen(false)} data-testid="bulk-stage-role-modal">
          <div className="bg-[var(--paper)] rounded-md border border-[var(--border)] p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline">Bulk edit</div>
                <h3 className="font-heading text-xl mt-1" style={{ fontWeight: 500 }}>
                  Stage roles ({activeMembers.length} teammates)
                </h3>
                <p className="text-[12px] text-[var(--ink-3)] mt-1">
                  Change any row&apos;s stage role. Only changed rows are PATCHed on save.
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-[var(--ink-3)] hover:text-[var(--ink)]">×</button>
            </div>
            <table className="w-full text-[13px]" data-testid="bulk-stage-role-table">
              <thead>
                <tr className="text-left text-[var(--ink-3)] border-b border-[var(--border)]">
                  <th className="py-1.5 font-normal">Teammate</th>
                  <th className="py-1.5 font-normal">Auth role</th>
                  <th className="py-1.5 font-normal">Stage role</th>
                </tr>
              </thead>
              <tbody>
                {activeMembers.map((m) => {
                  const changed = (m.stage_role || "") !== (draft[m.user_id] || "");
                  return (
                    <tr key={m.user_id} className="border-b border-[var(--border)]">
                      <td className="py-1.5">
                        <div className="font-medium">{m.name || (m.email || "").split("@")[0]}</div>
                        <div className="text-[10px] text-[var(--ink-3)] font-mono">{m.email}</div>
                      </td>
                      <td className="py-1.5 text-[11px]">{m.role}</td>
                      <td className="py-1.5">
                        <select
                          value={draft[m.user_id] || ""}
                          onChange={(e) => setDraft({ ...draft, [m.user_id]: e.target.value })}
                          data-testid={`bulk-stage-role-select-${m.user_id}`}
                          className={`bg-white border rounded px-2 py-1 text-[12px] focus:outline-none ${changed ? "border-[var(--moss)]" : "border-[var(--border)]"}`}
                        >
                          {STAGE_ROLES.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                        {changed && <span className="text-[10px] text-[var(--moss)] ml-2">changed</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex gap-2 pt-4 border-t border-[var(--border)] mt-4">
              <button className="btn-secondary flex-1 justify-center" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn-primary flex-1 justify-center" onClick={save} disabled={saving}
                      data-testid="bulk-stage-role-save">
                {saving ? "Saving…" : "Save all changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

